'use client';

import { useCallback, useEffect, useState } from 'react';

import { TrackedButton } from '@/components';
import { useFooter, useIssuePage } from '@/contexts';

interface IssueActionButtonsProps {
  isDiscouraged: boolean;
  buttonText: string;
  handleAction: () => void;
}

const IssueActionButtons = ({
  isDiscouraged,
  buttonText,
  handleAction,
}: IssueActionButtonsProps) => {
  const { pageState, isCreateModeWithNoInfo, getIsVoteMode } = useIssuePage();

  const { clearButtonPrimaryProps, buttonPrimaryProps, setButtonPrimaryProps } =
    useFooter();

  const [isLoading, setIsLoading] = useState(false);

  const handleClick = useCallback(async () => {
    setIsLoading(true);
    try {
      handleAction();
    } finally {
      setIsLoading(false);
    }
  }, [handleAction]);

  useEffect(() => {
    setButtonPrimaryProps({
      children: buttonText,
      onClick: handleClick,
      loading: isLoading,
      variant: isDiscouraged ? 'outline' : 'primary',
      trackingName: 'issue_action_buttons_click',
      trackingProperties: {
        buttonText: buttonText,
      },
      hidden:
        isCreateModeWithNoInfo(pageState) || getIsVoteMode() ? true : undefined,
    });
    return () => clearButtonPrimaryProps();
  }, [
    buttonText,
    clearButtonPrimaryProps,
    isDiscouraged,
    getIsVoteMode,
    handleClick,
    isCreateModeWithNoInfo,
    pageState,
    setButtonPrimaryProps,
    isLoading,
  ]);

  return (
    <TrackedButton
      size="lg"
      fullWidth
      disabled={isCreateModeWithNoInfo(pageState)}
      visibleFrom="md"
      {...buttonPrimaryProps}
    >
      {buttonText}
    </TrackedButton>
  );
};

export { IssueActionButtons };
